import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CheckCircle,
  XCircle,
  RefreshCw,
  Clock,
  FileText,
  Rocket,
  Database,
  Zap,
  X,
  AlertTriangle
} from 'lucide-react';
import { sseService } from '../../services/sseService';
import apiService from '../../services/api';
import {
  IDocumentInfo,
  ProcessingEvent,
  ChunkPreview,
  RAGProcessingState,
  CreateRAGRealtimeResponse
} from '../../types';

interface RAGProcessingModalProps {
  visible: boolean;
  document: IDocumentInfo | null;
  onClose: () => void;
  onComplete: () => void;
  strategy?: 'fixed' | 'semantic' | 'paragraph';
  chunkSize?: number;
  chunkOverlap?: number;
  reprocess?: boolean;
}

const RAGProcessingModal: React.FC<RAGProcessingModalProps> = ({
  visible,
  document,
  onClose,
  onComplete,
  strategy = 'semantic',
  chunkSize = 1000,
  chunkOverlap = 200,
  reprocess = false
}) => {
  const [state, setState] = useState<RAGProcessingState>({
    sessionId: null,
    connected: false,
    overallProgress: 0,
    currentStage: 'Initializing...',
    stats: {
      totalChunks: 0,
      chunksCreated: 0,
      embeddingsGenerated: 0,
      chunksStored: 0,
      elapsedTime: 0,
      processingSpeed: 0,
      averageChunkSize: 0,
      embeddingDimensions: 0
    },
    chunks: [],
    events: [],
    status: 'idle',
    error: null
  });

  const [startTime, setStartTime] = useState<number>(0);
  const chunksEndRef = useRef<HTMLDivElement>(null);
  const eventsEndRef = useRef<HTMLDivElement>(null);

  // Update elapsed time
  useEffect(() => {
    if (state.status === 'processing') {
      const interval = setInterval(() => {
        setState(prev => ({
          ...prev,
          stats: {
            ...prev.stats,
            elapsedTime: Date.now() - startTime,
            processingSpeed: prev.stats.chunksCreated > 0
              ? (prev.stats.chunksCreated / ((Date.now() - startTime) / 1000))
              : 0
          }
        }));
      }, 100);

      return () => clearInterval(interval);
    }
  }, [state.status, startTime]);

  // Auto-scroll chunks to bottom when new ones are added
  useEffect(() => {
    if (chunksEndRef.current && state.chunks.length > 0) {
      chunksEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [state.chunks.length]);

  // Auto-scroll events to bottom when new ones are added
  useEffect(() => {
    if (eventsEndRef.current && state.events.length > 0) {
      eventsEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [state.events.length]);

  // Cleanup: disconnect SSE on unmount
  useEffect(() => {
    return () => {
      if (state.sessionId && document) {
        console.log('🧹 Cleaning up SSE connection');
        sseService.disconnect(`${document._id}_${state.sessionId}`);
      }
    };
  }, [state.sessionId, document]);

  // Handle SSE events
  const handleEvent = useCallback((event: ProcessingEvent) => {
    console.log('🎯 handleEvent called with:', {
      eventType: event.eventType,
      stage: event.stage,
      progress: event.progress,
      message: event.message,
      metadata: event.metadata
    });

    // Log specifically for embedding events
    if (event.eventType === 'embedding') {
      console.log('🔥 EMBEDDING EVENT RECEIVED:', event.stage, 'Progress:', event.progress);
    }

    setState(prev => {
      try {
        console.log('📊 Updating state from:', prev.overallProgress, '% to', event.progress, '%');
        const newState = { ...prev };

        // Update progress
        newState.overallProgress = event.progress;
        newState.currentStage = event.message;

        // Add event to history
        newState.events = [...prev.events, event];

        // Update stats based on event type
        if (event.eventType === 'initialization') {
          if (event.stage === 'started') {
            console.log('🏁 Initialization started');
            newState.stats = {
              ...prev.stats,
              totalChunks: 0,
              chunksCreated: 0,
              embeddingsGenerated: 0,
              chunksStored: 0
            };
            newState.chunks = [];
            newState.events = [event];
          } else if (event.stage === 'cleanup') {
            newState.currentStage = 'Reprocessing: Clearing old data...';
          }
        }

        if (event.eventType === 'chunking') {
          if (event.stage === 'started') {
            newState.currentStage = 'Chunking document...';
          } else if (event.stage === 'chunk_created') {
            const chunkIndex = event.metadata.chunkIndex ?? prev.chunks.length;
            const totalChunks = event.metadata.totalChunks ?? prev.stats.totalChunks;

            newState.stats = {
              ...prev.stats,
              totalChunks,
              chunksCreated: chunkIndex + 1
            };
            console.log(`📦 Chunk ${chunkIndex + 1}/${totalChunks} created, stats updated`);

            const existingChunkIndex = prev.chunks.findIndex(c => c.index === chunkIndex);
            const chunkPreview: ChunkPreview = {
              index: chunkIndex,
              text: event.metadata.chunkText || '',
              size: event.metadata.chunkSize || 0,
              status: 'created'
            };

            if (existingChunkIndex >= 0) {
              newState.chunks = [...prev.chunks];
              newState.chunks[existingChunkIndex] = chunkPreview;
            } else {
              newState.chunks = [...prev.chunks, chunkPreview];
            }
          }
        }

        if (event.eventType === 'embedding') {
          if (event.stage === 'started') {
            newState.currentStage = 'Generating embeddings...';
            console.log('🚀 Embedding generation started');
          } else if (event.stage === 'retry') {
            const attempt = event.metadata.attempt || 1;
            const chunkIndex = event.metadata.chunkIndex ?? 0;
            newState.currentStage = `Retrying chunk ${chunkIndex + 1} (attempt ${attempt})...`;
            console.log(`🔄 Retry: ${event.message}`);
          } else if (event.stage === 'embedding_generated') {
            const chunkIndex = event.metadata.chunkIndex ?? 0;
            const totalChunks = event.metadata.totalChunks ?? prev.stats.totalChunks;
            console.log(`⚡ Processing embedding for chunk ${chunkIndex}, progress: ${event.progress}%`);
            console.log(`⚡ Previous stats:`, prev.stats);
            console.log(`⚡ Event metadata:`, event.metadata);

            newState.stats = {
              totalChunks: totalChunks,
              chunksCreated: prev.stats.chunksCreated,
              embeddingsGenerated: chunkIndex + 1,
              chunksStored: prev.stats.chunksStored,
              elapsedTime: prev.stats.elapsedTime,
              processingSpeed: prev.stats.processingSpeed,
              averageChunkSize: prev.stats.averageChunkSize,
              embeddingDimensions: event.metadata.embeddingDimensions || prev.stats.embeddingDimensions
            };
            console.log(`⚡ NEW stats after update:`, newState.stats);
            console.log(`⚡ Embeddings: ${newState.stats.embeddingsGenerated}/${totalChunks}`);

            const currentChunks = newState.chunks.length > 0 ? newState.chunks : prev.chunks;
            newState.chunks = currentChunks.map(c =>
              c.index === chunkIndex
                ? { ...c, status: 'embedding' as const, hasEmbedding: true }
                : c
            );
          }
        }

        if (event.eventType === 'storage') {
          if (event.stage === 'started') {
            newState.currentStage = 'Storing chunks in database...';
            console.log('💾 Storage started');
          } else if (event.stage === 'chunks_stored') {
            newState.stats = {
              ...prev.stats,
              chunksStored: event.metadata.totalChunks || prev.stats.totalChunks
            };
            console.log(`💾 ${newState.stats.chunksStored} chunks stored`);
            newState.chunks = newState.chunks.map(c => ({ ...c, status: 'stored' as const }));
          } else if (event.stage === 'metadata_updated') {
            newState.currentStage = 'Finalizing...';
          }
        }

        if (event.eventType === 'complete') {
          console.log('🎉 COMPLETE EVENT - Setting status to completed and progress to 100%');
          newState.status = 'completed';
          newState.overallProgress = 100;
          newState.currentStage = 'Completed!';
          newState.stats = {
            ...prev.stats,
            averageChunkSize: event.metadata.averageChunkSize || prev.stats.averageChunkSize,
            elapsedTime: event.metadata.processingTime || (Date.now() - startTime)
          };
          console.log('🎉 Final stats:', newState.stats);
        }

        if (event.eventType === 'error') {
          console.error('❌ ERROR EVENT received:', event);
          newState.status = 'error';

          let errorMsg = event.metadata.error || event.message;
          if (event.stage === 'validation_failed') {
            errorMsg = `Configuration Error: ${errorMsg}`;
          } else if (event.stage === 'concurrent_processing') {
            errorMsg = `Concurrent Processing: ${errorMsg}. Please wait for the current process to complete.`;
          } else if (event.metadata.rolledBack) {
            errorMsg = `${errorMsg} (Changes rolled back)`;
          }

          newState.error = errorMsg;
          newState.currentStage = 'Failed';
        }

        console.log('✅ State update successful, returning newState');
        return newState;
      } catch (error) {
        console.error('❌ ERROR in handleEvent setState:', error);
        console.error('Event that caused error:', event);
        console.error('Previous state:', prev);
        return prev;
      }
    });
  }, [startTime]);

  // Start RAG processing
  const startProcessing = useCallback(async () => {
    if (!document) return;

    try {
      setState(prev => ({ ...prev, status: 'connecting', error: null }));
      setStartTime(Date.now());

      const response = await apiService.post<CreateRAGRealtimeResponse>(
        `/documents/${document._id}/rag/create-realtime`,
        {
          strategy,
          chunkSize,
          chunkOverlap,
          reprocess
        }
      );

      console.log('API Response:', response);

      const apiResponse = response.data as any;
      const sessionId = apiResponse?.data?.sessionId;

      if (!sessionId) {
        console.error('No sessionId in response:', response);
        throw new Error('No session ID received from server');
      }

      console.log('Session ID:', sessionId);

      setState(prev => ({
        ...prev,
        sessionId,
        status: 'connecting',
        connected: false
      }));

      console.log('🔌 Setting up SSE connection with callbacks:', {
        hasOnEvent: !!handleEvent,
        documentId: document._id,
        sessionId
      });

      await new Promise(resolve => setTimeout(resolve, 800));

      sseService.connect(document._id, sessionId, {
        onEvent: (event) => {
          console.log('🔔 onEvent callback triggered, calling handleEvent');
          handleEvent(event);
        },
        onConnect: () => {
          console.log('✅ SSE Connected - now processing');
          setState(prev => ({
            ...prev,
            connected: true,
            status: 'processing'
          }));
        },
        onError: (error) => {
          console.error('❌ SSE Error:', error);
          setState(prev => ({
            ...prev,
            status: 'error',
            error: error.message,
            connected: false
          }));
        },
        onComplete: () => {
          console.log('🎉 SSE Complete');
          setTimeout(() => {
            onComplete();
          }, 2000);
        }
      });

    } catch (error: any) {
      console.error('Error starting RAG processing:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to start processing';

      setState(prev => ({
        ...prev,
        status: 'error',
        error: errorMessage,
        currentStage: 'Failed to start'
      }));

      if (errorMessage.includes('already in progress')) {
        setState(prev => ({
          ...prev,
          error: 'RAG processing is already running for this document. Please wait or refresh to see the latest status.'
        }));
      } else if (errorMessage.includes('GEMINI_API_KEY')) {
        setState(prev => ({
          ...prev,
          error: 'API configuration error. Please contact support.'
        }));
      }
    }
  }, [document, strategy, chunkSize, chunkOverlap, reprocess, handleEvent, onComplete]);

  // Start processing when modal opens
  useEffect(() => {
    if (visible && document && state.status === 'idle') {
      startProcessing();
    }
  }, [visible, document, state.status, startProcessing]);

  // Cleanup on unmount or close
  useEffect(() => {
    return () => {
      if (state.sessionId && document) {
        sseService.disconnect(`${document._id}_${state.sessionId}`);
      }
    };
  }, [state.sessionId, document]);

  const handleClose = () => {
    if (state.sessionId && document) {
      sseService.disconnect(`${document._id}_${state.sessionId}`);
    }
    onClose();
  };

  const getProgressBarColor = () => {
    if (state.status === 'completed') return 'bg-green-500';
    if (state.status === 'error') return 'bg-red-500';
    return 'bg-blue-600';
  };

  const getStageIcon = (eventType: string) => {
    switch (eventType) {
      case 'initialization':
        return <RefreshCw className="w-3.5 h-3.5 animate-spin text-blue-500" />;
      case 'chunking':
        return <FileText className="w-3.5 h-3.5 text-blue-500" />;
      case 'embedding':
        return <Rocket className="w-3.5 h-3.5 text-blue-500" />;
      case 'storage':
        return <Database className="w-3.5 h-3.5 text-blue-500" />;
      case 'complete':
        return <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
      case 'error':
        return <XCircle className="w-3.5 h-3.5 text-red-500" />;
      default:
        return <RefreshCw className="w-3.5 h-3.5 text-blue-500" />;
    }
  };

  const getChunkStatusClasses = (status: ChunkPreview['status']) => {
    switch (status) {
      case 'created':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'embedding':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'stored':
        return 'bg-green-50 text-green-700 border-green-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getTimelineDotColor = (eventType: string) => {
    if (eventType === 'error') return 'bg-red-500';
    if (eventType === 'complete') return 'bg-green-500';
    return 'bg-blue-500';
  };

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 font-semibold text-gray-800">
            <Zap className="w-5 h-5 text-yellow-500" />
            <span>Real-Time RAG Processing</span>
          </div>
          <button
            onClick={handleClose}
            disabled={state.status === 'processing'}
            className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Document Info */}
          {document && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <dl className="grid grid-cols-[160px_1fr] gap-2 text-sm">
                <dt className="text-gray-500 font-medium">Document</dt>
                <dd className="text-gray-800">{document.originalName}</dd>
                <dt className="text-gray-500 font-medium">Strategy</dt>
                <dd className="text-gray-800">{strategy}</dd>
                <dt className="text-gray-500 font-medium">Chunk Size</dt>
                <dd className="text-gray-800">{chunkSize}</dd>
              </dl>
            </div>
          )}

          {/* Error Alert */}
          {state.error && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold mb-1">Processing Error</p>
                <p>{state.error}</p>
                {state.error.includes('rolled back') && (
                  <p className="mt-2 text-xs text-red-600">
                    All partial changes have been cleaned up. You can safely retry the operation.
                  </p>
                )}
              </div>
              {state.status === 'error' && (
                <button
                  onClick={() => {
                    setState(prev => ({ ...prev, status: 'idle', error: null }));
                    startProcessing();
                  }}
                  className="flex-shrink-0 px-3 py-1 text-xs font-medium border border-red-300 rounded text-red-700 hover:bg-red-100"
                >
                  Retry
                </button>
              )}
            </div>
          )}

          {/* Progress Bar */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2">
            <p className="text-sm font-semibold text-gray-700">Overall Progress</p>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className={`${getProgressBarColor()} h-2.5 rounded-full transition-all duration-300`}
                style={{ width: `${state.overallProgress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500">{state.currentStage}</p>
          </div>

          {/* Statistics */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">Processing Statistics</p>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Total Chunks
                </p>
                <p className="text-xl font-bold text-gray-800">{state.stats.totalChunks}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Chunks Created</p>
                <p className="text-xl font-bold text-green-700">
                  {state.stats.chunksCreated}
                  <span className="text-sm font-normal text-gray-400"> / {state.stats.totalChunks}</span>
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Rocket className="w-3 h-3" /> Embeddings
                </p>
                <p className="text-xl font-bold text-blue-600">
                  {state.stats.embeddingsGenerated}
                  <span className="text-sm font-normal text-gray-400"> / {state.stats.totalChunks}</span>
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Elapsed Time
                </p>
                <p className="text-xl font-bold text-gray-800">{formatTime(state.stats.elapsedTime)}</p>
              </div>
            </div>

            {state.stats.processingSpeed > 0 && (
              <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
                <div>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Zap className="w-3 h-3" /> Speed
                  </p>
                  <p className="text-xl font-bold text-gray-800">
                    {state.stats.processingSpeed.toFixed(2)}
                    <span className="text-sm font-normal text-gray-400"> chunks/s</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Avg Chunk Size</p>
                  <p className="text-xl font-bold text-gray-800">
                    {state.stats.averageChunkSize || '-'}
                    {state.stats.averageChunkSize ? <span className="text-sm font-normal text-gray-400"> chars</span> : ''}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Embedding Dims</p>
                  <p className="text-xl font-bold text-gray-800">{state.stats.embeddingDimensions || '-'}</p>
                </div>
              </div>
            )}
          </div>

          {/* Chunks + Events panels */}
          <div className="grid grid-cols-2 gap-4">
            {/* Live Chunks Panel */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
              <p className="text-sm font-semibold text-gray-700 mb-3">Chunks ({state.chunks.length})</p>
              {state.chunks.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <p>No chunks yet</p>
                </div>
              ) : (
                <>
                  <ul className="space-y-2">
                    {state.chunks.map((chunk) => (
                      <li key={chunk.index} className="border border-gray-100 rounded p-2">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getChunkStatusClasses(chunk.status)}`}>
                            Chunk {chunk.index + 1}
                          </span>
                          <span className="text-xs text-gray-400">{chunk.size} chars</span>
                          {chunk.hasEmbedding && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-purple-50 text-purple-700 border-purple-200">
                              <CheckCircle className="w-3 h-3" />
                              Embedded
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 line-clamp-2">{chunk.text}</p>
                      </li>
                    ))}
                  </ul>
                  <div ref={chunksEndRef} />
                </>
              )}
            </div>

            {/* Events Timeline */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
              <p className="text-sm font-semibold text-gray-700 mb-3">Event Log ({state.events.length})</p>
              {state.events.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <p>No events yet</p>
                </div>
              ) : (
                <>
                  <ul className="space-y-2">
                    {state.events.slice().reverse().map((event, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <div className="flex flex-col items-center mt-0.5">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${getTimelineDotColor(event.eventType)}`} />
                          {idx < state.events.length - 1 && (
                            <div className="w-px flex-1 bg-gray-200 mt-1 min-h-[16px]" />
                          )}
                        </div>
                        <div className="flex items-start gap-1.5 min-w-0">
                          <span className="flex-shrink-0 mt-0.5">{getStageIcon(event.eventType)}</span>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-800 truncate">{event.message}</p>
                            <p className="text-xs text-gray-400">
                              {new Date(event.timestamp).toLocaleTimeString()} — {event.progress}%
                            </p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div ref={eventsEndRef} />
                </>
              )}
            </div>
          </div>

          {/* Connection Status */}
          {state.status === 'processing' && (
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
              <span>Connected to real-time stream</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <button
            onClick={handleClose}
            disabled={state.status === 'processing'}
            className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {state.status === 'processing' ? 'Processing...' : 'Close'}
          </button>

          {state.status === 'error' && (
            <button
              onClick={() => {
                setState(prev => ({
                  ...prev,
                  status: 'idle',
                  error: null,
                  events: [],
                  chunks: [],
                  overallProgress: 0
                }));
                startProcessing();
              }}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          )}

          {state.status === 'completed' && (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RAGProcessingModal;
